/**
 * Types for the kubernetes-workloads plugin.
 */

// ── 环境相关 ──

/** 环境 Resource 注解 key 常量 */
export const K8S_ENVIRONMENT_ANNOTATIONS = {
  POWERED_BY: 'hose.io/powered-by',
  K8S_CLUSTER: 'hose.io/k8s-cluster',
  K8S_NAMESPACE: 'hose.io/k8s-namespace',
  ENVIRONMENT_TYPE: 'hose.io/environment-type',
  ENVIRONMENT_NAME: 'hose.io/environment-name',
  IMAGE_REGISTRY: 'hose.io/image-registry',
  TEKTON_CLUSTER: 'hose.io/tekton-cluster',
  TEKTON_NAMESPACE: 'hose.io/tekton-namespace',
} as const;

/** 环境信息（从 Resource 实体解析） */
export interface EnvironmentInfo {
  /** Resource 实体名称 */
  name: string;
  /** 显示标签（如: 华为原生） */
  label: string;
  /** 环境类型（production / test / staging） */
  type: string;
  /** K8s 集群名称 */
  cluster: string;
  /** K8s 命名空间 */
  namespace: string;
  /** 镜像仓库（可选） */
  imageRegistry?: string;
  /** Tekton Pipeline 运行集群 */
  tektonCluster?: string;
  /** Tekton Pipeline 命名空间 */
  tektonNamespace?: string;
  /**
   * 原始 Resource 实体 JSON（由 useEnvironments 在加载列表时缓存）。
   * 在触发 Pipeline 时直接注入为 k8s-resource 参数，无需二次请求 Catalog API。
   */
  entityJson?: string;
}

// ── 工作负载相关 ──

/** 工作负载类型 */
export type WorkloadKind =
  | 'Deployment'
  | 'StatefulSet'
  | 'DaemonSet'
  | 'CronJob'
  | 'Job';

/** 工作负载信息 */
export interface WorkloadInfo {
  /** 名称 */
  name: string;
  /** 命名空间 */
  namespace: string;
  /** 类型 */
  kind: WorkloadKind;
  /** 期望副本数 */
  replicas: number;
  /** 就绪副本数 */
  readyReplicas: number;
  /** 可用副本数 */
  availableReplicas: number;
  /** 镜像列表（兼容用，取自 containers） */
  images: string[];
  /** 容器列表（名称 + 镜像），用于多容器工作负载的精确操作 */
  containers?: Array<{ name: string; image: string }>;
  /** 创建时间 */
  createdAt: string;
  /** 年龄 */
  age: string;
  /** 更新策略 */
  strategy?: string;
  /** K8s Labels */
  labels?: Record<string, string>;
  /** K8s Annotations */
  annotations?: Record<string, string>;
  /** Conditions */
  conditions?: WorkloadCondition[];
}

/** 工作负载状态条件 */
export interface WorkloadCondition {
  type: string;
  status: string;
  reason?: string;
  message?: string;
  lastUpdateTime?: string;
}

// ── Pod 相关 ──

/** Pod 状态 */
export type PodPhase = 'Running' | 'Pending' | 'Failed' | 'Succeeded' | 'Unknown';

/** Pod 信息 */
export interface PodInfo {
  /** Pod 名称 */
  name: string;
  /** 命名空间 */
  namespace: string;
  /** 状态 */
  status: PodPhase;
  /** 就绪状态 (e.g. "2/2") */
  ready: string;
  /** 重启次数 */
  restarts: number;
  /** 年龄 */
  age: string;
  /** 节点名称 */
  node: string;
  /** Pod IP */
  ip?: string;
  /** 容器列表 */
  containers: ContainerInfo[];
}

/** 容器信息 */
export interface ContainerInfo {
  /** 容器名称 */
  name: string;
  /** 镜像 */
  image: string;
  /** 是否就绪 */
  ready: boolean;
  /** 重启次数 */
  restartCount: number;
  /** 运行状态 */
  state: 'running' | 'waiting' | 'terminated';
  /** 状态原因 */
  reason?: string;
}

// ── Events 相关 ──

/** K8s Event 信息 */
export interface EventInfo {
  /** 事件类型 (Normal / Warning) */
  type: string;
  /** 原因 */
  reason: string;
  /** 消息 */
  message: string;
  /** 关联对象 */
  involvedObject: {
    kind: string;
    name: string;
  };
  /** 发生次数 */
  count: number;
  /** 首次发生时间 */
  firstTimestamp: string;
  /** 最后发生时间 */
  lastTimestamp: string;
  /** 年龄 */
  age: string;
}

// ── 资源类型 Tab ──

/** 资源类型 Tab Key */
export type ResourceTabKey = 'workloads' | 'pods' | 'network' | 'storage' | 'config';

/** 资源类型 Tab 配置 */
export interface ResourceTabConfig {
  key: ResourceTabKey;
  label: string;
  icon: string;
}

export const RESOURCE_TABS: ResourceTabConfig[] = [
  { key: 'workloads', label: '工作负载', icon: '📋' },
  { key: 'pods', label: '容器组', icon: '🔲' },
  { key: 'network', label: '网络', icon: '🌐' },
  { key: 'storage', label: '存储', icon: '💾' },
  { key: 'config', label: '配置', icon: '⚙' },
];
